import type {
  VisioReviewConfig,
  VisioReviewFilterOptions,
  VisioReviewInteractionsResponse,
  VisioReviewInteractionsSortBy,
  VisioReviewOperatorCommentsResponse,
  VisioReviewOperatorDetail,
  VisioReviewOperatorInteractionsResponse,
  VisioReviewOperatorsChartsResponse,
  VisioReviewOperatorsResponse,
  VisioReviewOperatorsSortBy,
  VisioReviewRecordingUrlResponse,
  VisioReviewSortDir,
  VisioReviewStoresResponse,
  VisioReviewStoresSortBy,
  VisioReviewTranscriptResponse,
} from '@gozoki/api-types'
import { gozokiApiWithAuth } from '../gozokiApi'

export interface FetchVisioReviewInteractionsParams {
  dateStart?: string
  dateEnd: string
  page?: number
  pageSize?: number
  storeId?: number
  operatorId?: number
  minGrade?: number
  maxGrade?: number
  noGrade?: boolean
  notValidated?: boolean
  interactionTypes?: string
  search?: string
  sortBy?: VisioReviewInteractionsSortBy
  sortDir?: VisioReviewSortDir
}

export const fetchVisioReviewInteractions = async (params: FetchVisioReviewInteractionsParams) => {
  const res = await gozokiApiWithAuth.get<VisioReviewInteractionsResponse>(
    '/visio-reviews/interactions',
    { params },
  )
  return res.data
}

export interface FetchVisioReviewOperatorsParams {
  dateStart?: string
  dateEnd: string
  previousStart?: string
  previousEnd?: string
  page?: number
  pageSize?: number
  search?: string
  sortBy?: VisioReviewOperatorsSortBy
  sortDir?: VisioReviewSortDir
  interactionTypes?: string
}

export const fetchVisioReviewOperators = async (params: FetchVisioReviewOperatorsParams) => {
  const res = await gozokiApiWithAuth.get<VisioReviewOperatorsResponse>(
    '/visio-reviews/operators',
    { params },
  )
  return res.data
}

export const fetchVisioReviewOperatorCharts = async (params: {
  dateStart?: string
  dateEnd: string
  previousStart?: string
  previousEnd?: string
  interactionTypes?: string
}) => {
  const res = await gozokiApiWithAuth.get<VisioReviewOperatorsChartsResponse>(
    '/visio-reviews/operators/charts',
    { params },
  )
  return res.data
}

export const fetchVisioReviewOperatorDetail = async (
  userId: number,
  params: { dateStart?: string; dateEnd: string; interactionTypes?: string },
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewOperatorDetail>(
    `/visio-reviews/operators/${userId}`,
    { params },
  )
  return res.data
}

export interface FetchVisioReviewOperatorInteractionsParams {
  dateStart?: string
  dateEnd: string
  page?: number
  pageSize?: number
  interactionTypes?: string
}

export const fetchVisioReviewOperatorInteractions = async (
  userId: number,
  params: FetchVisioReviewOperatorInteractionsParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewOperatorInteractionsResponse>(
    `/visio-reviews/operators/${userId}/interactions`,
    { params },
  )
  return res.data
}

export interface FetchVisioReviewOperatorCommentsParams {
  dateStart?: string
  dateEnd: string
  page?: number
  pageSize?: number
  interactionTypes?: string
}

export const fetchVisioReviewOperatorComments = async (
  userId: number,
  params: FetchVisioReviewOperatorCommentsParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewOperatorCommentsResponse>(
    `/visio-reviews/operators/${userId}/comments`,
    { params },
  )
  return res.data
}

export interface FetchVisioReviewStoreListParams {
  dateStart?: string
  dateEnd: string
  previousStart?: string
  previousEnd?: string
  page?: number
  pageSize?: number
  search?: string
  sortBy?: VisioReviewStoresSortBy
  sortDir?: VisioReviewSortDir
  interactionTypes?: string
}

export const fetchVisioReviewStoreList = async (params: FetchVisioReviewStoreListParams) => {
  const res = await gozokiApiWithAuth.get<VisioReviewStoresResponse>('/visio-reviews/stores', {
    params,
  })
  return res.data
}

export const fetchVisioReviewRecordingUrl = async (reviewId: number) => {
  const res = await gozokiApiWithAuth.get<VisioReviewRecordingUrlResponse>(
    `/visio-reviews/${reviewId}/recording-url`,
  )
  return res.data
}

export const fetchVisioReviewTranscript = async (reviewId: number) => {
  const res = await gozokiApiWithAuth.get<VisioReviewTranscriptResponse>(
    `/visio-reviews/${reviewId}/transcript`,
  )
  return res.data
}

export const fetchVisioReviewConfig = async () => {
  const res = await gozokiApiWithAuth.get<VisioReviewConfig>('/visio-reviews/config')
  return res.data
}

export interface FetchVisioReviewInteractionsToEvaluateParams {
  dateStart?: string
  dateEnd?: string
  page?: number
  pageSize?: number
  sortBy?: VisioReviewInteractionsSortBy
  sortDir?: VisioReviewSortDir
  storeId?: number
  operatorId?: number
  minGrade?: number
  maxGrade?: number
  noGrade?: boolean
  notValidated?: boolean
  interactionTypes?: string
  search?: string
}

export const fetchVisioReviewInteractionsToEvaluate = async (
  params: FetchVisioReviewInteractionsToEvaluateParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewInteractionsResponse>(
    '/visio-reviews/to-evaluate',
    { params },
  )
  return res.data
}

export interface FetchVisioReviewFilterOptionsParams {
  dateStart?: string
  dateEnd: string
  interactionTypes?: string
}

export const fetchVisioReviewFilterOptions = async (
  params: FetchVisioReviewFilterOptionsParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewFilterOptions>(
    '/visio-reviews/filter-options',
    { params },
  )
  return res.data
}
